/**
 * CVImportDialog
 *
 * Upload a CV (PDF/DOCX) → AI extracts profile data → user reviews and picks
 * what to keep → selected items are written to the profile.
 *
 * Props:
 *   open, onClose
 *   currentValues  {object}  current profile scalar values (headline, bio, …) —
 *                            fields that already have a value start unticked
 *   onApplied(res) {fn}      called with the cv-apply response
 */
import React, { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Alert, LinearProgress, Checkbox, FormControlLabel, Chip, Stack, Divider,
  Accordion, AccordionSummary, AccordionDetails, Tooltip, TextField,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import CreditBalance from 'components/common/billing/CreditBalance';
import PaymentDialog from 'components/common/billing/PaymentDialog';
import useBillingBalance from 'components/common/billing/useBillingBalance';
import { useUserContext } from 'shared/contexts/UserContext';

const PROFILE_FIELDS = [
  ['position', 'Position'],
  ['headline', 'Headline'],
  ['bio', 'Bio'],
  ['resume', 'Resume summary'],
  ['city', 'City'],
  ['website', 'Website'],
  ['orcid', 'ORCID'],
  ['google_scholar', 'Google Scholar'],
];

const LIST_SECTIONS = [
  ['education', 'Education', (e) => [`${e.degree || ''} ${e.field_of_study ? `in ${e.field_of_study}` : ''}`.trim(), [e.institution, period(e)].filter(Boolean).join(' · ')]],
  ['work_experience', 'Work experience', (w) => [w.position, [w.organization, period(w)].filter(Boolean).join(' · ')]],
  ['publications', 'Publications', (p) => [p.title, [p.journal, p.year, p.doi].filter(Boolean).join(' · ')]],
  ['projects', 'Projects', (p) => [p.title, [p.role, p.funding_source, period(p)].filter(Boolean).join(' · ')]],
  ['patents', 'Patents', (p) => [p.title, [p.patent_number, p.status].filter(Boolean).join(' · ')]],
];

function period(item) {
  if (!item.start && !item.end && !item.is_current) return '';
  return `${item.start || '?'}–${item.is_current ? 'present' : (item.end || '?')}`;
}

const isBlank = (v) => !v || ['False', 'false', 'None', 'null'].includes(String(v));

const STAT_FIELDS = [
  ['publications_count', 'Publications'],
  ['citations_count', 'Citations'],
  ['h_index', 'h-index'],
];

// Items fetched from a source whose matched author did not look like the user start unticked
const untrustedSources = (reports = []) => reports.filter((r) => r.status === 'warning').map((r) => r.source);
const fromUntrusted = (item, untrusted) => untrusted.some((src) => (item?.source || '').startsWith(src));

export default function CVImportDialog({ open, onClose, currentValues = {}, onApplied, mode = 'cv' }) {
  const isPublic = mode === 'public';
  const [step, setStep] = useState('upload');     // upload | parsing | review | applying
  const [file, setFile] = useState(null);
  const [ids, setIds] = useState({ orcid: '', google_scholar: '', website: '', use_ai: false });
  const [consent, setConsent] = useState(false);
  const [reports, setReports] = useState([]);
  const [importId, setImportId] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);            // selection state mirroring data
  const [paymentOpen, setPaymentOpen] = useState(false);

  const { balance, isUnlimited, refresh: refreshBalance } = useBillingBalance();
  const { user } = useUserContext();
  const isAdmin = user?.is_superuser || user?.is_staff || false;

  const reset = useCallback(() => {
    setStep('upload'); setFile(null); setError(null); setData(null); setSel(null);
    setConsent(false); setReports([]); setImportId(null);
  }, []);

  // Pre-fill the identifiers the profile already has
  React.useEffect(() => {
    if (open && isPublic) {
      setIds((v) => ({
        ...v,
        orcid: v.orcid || (isBlank(currentValues.orcid) ? '' : currentValues.orcid),
        google_scholar: v.google_scholar || (isBlank(currentValues.google_scholar) ? '' : currentValues.google_scholar),
        website: v.website || (isBlank(currentValues.website) ? '' : currentValues.website),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPublic]);

  const selectionFor = (d, untrusted = []) => ({
    profile: Object.fromEntries(
      PROFILE_FIELDS.map(([k]) => [k, !!d.profile?.[k] && isBlank(currentValues[k])])
    ),
    ...Object.fromEntries(LIST_SECTIONS.map(([k]) => [k, (d[k] || []).map((x) => !fromUntrusted(x, untrusted))])),
    skills: (d.skills || []).map((x) => !fromUntrusted(x, untrusted)),
    research_interests: (d.research_interests || []).map((x) => !fromUntrusted(x, untrusted)),
    stats: Object.fromEntries(STAT_FIELDS.map(([k]) => [
      k, d.stats?.[k] != null && !untrusted.includes(d.stats?.source),
    ])),
  });

  const handleFetchPublic = async () => {
    setStep('parsing');
    setError(null);
    try {
      const res = await axiosService.post('/user-profiles/me/public-import/', { ...ids, consent: true }, { timeout: 120000 });
      const d = res.data.data;
      setData(d);
      setReports(res.data.reports || []);
      setImportId(res.data.import_id);
      setSel(selectionFor(d, untrustedSources(res.data.reports)));
      setStep('review');
      if (ids.use_ai) refreshBalance();
    } catch (e) {
      const statusCode = e.response?.status;
      if (statusCode === 402) setPaymentOpen(true);
      let message;
      if (statusCode === 429) message = 'Too many imports in the last hour. Please try again later.';
      else if (e.response?.data?.error) message = e.response.data.error;
      else if (!e.isAxiosError) message = 'Your data was fetched but could not be displayed. Please try again; if it keeps happening, let us know.';
      else if (!e.response) message = e.code === 'ECONNABORTED'
        ? 'The public sources took too long to answer. Please try again in a minute.'
        : 'Could not reach the Otable server. Check your connection and try again.';
      else message = `The server could not complete the import (error ${statusCode}). Please try again; if it keeps happening, let us know.`;
      setError(message);
      if (!e.response) console.error('Public profile import failed', e);
      setStep('upload');
    }
  };

  const handleClose = () => {
    if (step === 'parsing' || step === 'applying') return;
    reset();
    onClose?.();
  };

  const handleParse = async () => {
    if (!file) return;
    setStep('parsing');
    setError(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axiosService.post('/user-profiles/me/cv-parse/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      const d = res.data.data;
      setData(d);
      setSel(selectionFor(d));
      setStep('review');
      refreshBalance();
    } catch (e) {
      if (e.response?.status === 402) setPaymentOpen(true);
      setError(e.response?.data?.error || 'Failed to read the CV. Please try again.');
      setStep('upload');
    }
  };

  const handleApply = async () => {
    const payload = {
      profile: Object.fromEntries(
        PROFILE_FIELDS.filter(([k]) => sel.profile[k]).map(([k]) => [k, data.profile[k]])
      ),
    };
    for (const key of [...LIST_SECTIONS.map(([k]) => k), 'skills', 'research_interests']) {
      payload[key] = (data[key] || []).filter((_, i) => sel[key][i]);
    }
    if (data.stats) {
      payload.stats = Object.fromEntries(STAT_FIELDS.filter(([k]) => sel.stats?.[k]).map(([k]) => [k, data.stats[k]]));
    }
    setStep('applying');
    setError(null);
    try {
      const res = await axiosService.post('/user-profiles/me/cv-apply/', { data: payload, import_id: importId });
      onApplied?.(res.data);
      reset();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update your profile.');
      setStep('review');
    }
  };

  const toggle = (key, i) => setSel((s) => ({ ...s, [key]: s[key].map((v, j) => (j === i ? !v : v)) }));
  const toggleAll = (key, value) => setSel((s) => ({ ...s, [key]: s[key].map(() => value) }));
  const toggleField = (k) => setSel((s) => ({ ...s, profile: { ...s.profile, [k]: !s.profile[k] } }));
  const toggleStat = (k) => setSel((s) => ({ ...s, stats: { ...s.stats, [k]: !s.stats[k] } }));

  const selectedCount = sel
    ? Object.values(sel.profile).filter(Boolean).length
      + Object.values(sel.stats || {}).filter(Boolean).length
      + Object.entries(sel).filter(([k]) => !['profile', 'stats'].includes(k)).reduce((n, [, arr]) => n + arr.filter(Boolean).length, 0)
    : 0;

  const sectionHeader = (key, label, count) => (
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Typography fontWeight={600}>{label}</Typography>
        <Chip size="small" label={`${sel[key].filter(Boolean).length}/${count}`} />
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={(e) => { e.stopPropagation(); toggleAll(key, !sel[key].every(Boolean)); }}>
          {sel[key].every(Boolean) ? 'Deselect all' : 'Select all'}
        </Button>
      </Box>
    </AccordionSummary>
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          <Box sx={{ flex: 1 }}>{isPublic ? 'Fill in my profile from public sources' : 'Import profile from CV'}</Box>
          <CreditBalance balance={balance} isUnlimited={isAdmin || isUnlimited} onClick={() => setPaymentOpen(true)} />
        </DialogTitle>

        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {isPublic && (step === 'upload' || step === 'parsing') && (
            <Stack spacing={2} sx={{ py: 1 }}>
              <Typography color="text.secondary">
                Fill in your profile from what is already public about you. You review every suggestion
                and choose what to add; nothing is saved before that.
              </Typography>
              <TextField
                label="ORCID iD" placeholder="0000-0002-1825-0097 or https://orcid.org/…" size="small" fullWidth
                value={ids.orcid} onChange={(e) => setIds((v) => ({ ...v, orcid: e.target.value }))}
                helperText="Your ORCID record (positions, education, works, funding) and, through it, OpenAlex (citations, h-index, research topics)."
              />
              <TextField
                label="Google Scholar profile link" placeholder="https://scholar.google.com/citations?user=…" size="small" fullWidth
                value={ids.google_scholar} onChange={(e) => setIds((v) => ({ ...v, google_scholar: e.target.value }))}
                helperText="Saved as a link on your profile. Google does not allow automated reading of Scholar pages."
              />
              <TextField
                label="Your web page" placeholder="https://your-lab.university.edu/people/you" size="small" fullWidth
                value={ids.website} onChange={(e) => setIds((v) => ({ ...v, website: e.target.value }))}
                helperText="Read once for structured data and ORCID / Scholar links."
              />
              {ids.website.trim() && (
                <FormControlLabel
                  control={<Checkbox checked={ids.use_ai} onChange={(e) => setIds((v) => ({ ...v, use_ai: e.target.checked }))} />}
                  label={<Typography variant="body2">Also read the page with AI to find positions, education and publications (about 1 credit)</Typography>}
                />
              )}
              <Alert severity="info" icon={false} sx={{ alignItems: 'flex-start' }}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} sx={{ pt: 0 }} />}
                  label={
                    <Typography variant="body2">
                      I agree that Otable fetches my <strong>public</strong> data, once, from ORCID and OpenAlex for the
                      ORCID iD above (or one found on my web page) and from the web page I entered, to suggest profile
                      entries. Nothing is added to my profile until I confirm, and a record of this import (sources,
                      identifiers and what I added) is kept in my account.
                    </Typography>
                  }
                />
              </Alert>
              {step === 'parsing' && (
                <Box>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary">Fetching your public profile…</Typography>
                </Box>
              )}
            </Stack>
          )}

          {!isPublic && (step === 'upload' || step === 'parsing') && (
            <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
              <Typography color="text.secondary" textAlign="center">
                Upload your CV and AI will extract your education, experience, publications and
                projects, and suggest skills and research areas. You review everything before
                it is added to your profile. Uses about 1 credit.
              </Typography>
              <input
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                style={{ display: 'none' }}
                id="cv-import-input"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setError(null); }}
                disabled={step === 'parsing'}
              />
              <label htmlFor="cv-import-input">
                <Button component="span" variant="outlined" startIcon={<UploadFileIcon />} disabled={step === 'parsing'}>
                  {file ? 'Choose a different file' : 'Choose CV (PDF or DOCX)'}
                </Button>
              </label>
              {file && <Typography variant="body2">{file.name}</Typography>}
              {step === 'parsing' && (
                <Box sx={{ width: '100%' }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary">Reading your CV… this can take up to a minute.</Typography>
                </Box>
              )}
            </Stack>
          )}

          {(step === 'review' || step === 'applying') && data && sel && (
            <Box>
              {reports.length > 0 && (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {reports.map((r, i) => (
                    <Alert key={i} severity={r.status === 'ok' ? 'success' : r.status === 'warning' ? 'warning' : r.status === 'error' ? 'error' : 'info'}
                           sx={{ py: 0.25 }}>
                      <strong>{r.source}</strong>
                      {r.matched_name ? ` · ${r.matched_name}` : ''}{r.detail ? ` · ${r.detail}` : ''}
                      {r.warning && <Box component="span" sx={{ display: 'block', mt: 0.5 }}>{r.warning}</Box>}
                    </Alert>
                  ))}
                </Stack>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tick what you want to add. Existing entries are never removed, and duplicates are skipped.
                Profile fields that already have a value are unticked by default.
              </Typography>

              {data.stats && STAT_FIELDS.some(([k]) => data.stats[k] != null) && (
                <Accordion defaultExpanded disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>Publication statistics</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>from {data.stats.source}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      {STAT_FIELDS.filter(([k]) => data.stats[k] != null).map(([k, label]) => (
                        <FormControlLabel key={k}
                          control={<Checkbox checked={!!sel.stats?.[k]} onChange={() => toggleStat(k)} />}
                          label={<Typography variant="body2"><strong>{data.stats[k]}</strong> {label}</Typography>}
                        />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {PROFILE_FIELDS.some(([k]) => data.profile?.[k]) && (
                <Accordion defaultExpanded disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>Profile details</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={0.5}>
                      {PROFILE_FIELDS.filter(([k]) => data.profile?.[k]).map(([k, label]) => (
                        <FormControlLabel
                          key={k}
                          sx={{ alignItems: 'flex-start' }}
                          control={<Checkbox checked={sel.profile[k]} onChange={() => toggleField(k)} sx={{ pt: 0.5 }} />}
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {label}
                                {!isBlank(currentValues[k]) && (
                                  <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                    replaces current value
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                {data.profile[k]}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {LIST_SECTIONS.filter(([k]) => data[k]?.length).map(([key, label, describe]) => (
                <Accordion key={key} disableGutters>
                  {sectionHeader(key, label, data[key].length)}
                  <AccordionDetails>
                    <Stack spacing={0.5}>
                      {data[key].map((item, i) => {
                        const [primary, secondary] = describe(item);
                        return (
                          <FormControlLabel
                            key={i}
                            sx={{ alignItems: 'flex-start' }}
                            control={<Checkbox checked={sel[key][i]} onChange={() => toggle(key, i)} sx={{ pt: 0.5 }} />}
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{primary}</Typography>
                                {secondary && <Typography variant="caption" color="text.secondary">{secondary}</Typography>}
                                {item.source && <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>from {item.source}</Typography>}
                              </Box>
                            }
                          />
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}

              {(data.skills?.length > 0 || data.research_interests?.length > 0) && (
                <Box sx={{ mt: 2 }}>
                  {data.skills?.length > 0 && (
                    <>
                      <Typography fontWeight={600} sx={{ mb: 1 }}>
                        Skills <Typography component="span" variant="caption" color="text.secondary">(AI-generated · click to toggle)</Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {data.skills.map((s, i) => (
                          <Tooltip key={i} title={`Proficiency ${s.proficiency}/5 · ${s.years_exp} yrs`}>
                            <Chip
                              label={s.name}
                              color={sel.skills[i] ? 'primary' : 'default'}
                              variant={sel.skills[i] ? 'filled' : 'outlined'}
                              onClick={() => toggle('skills', i)}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </>
                  )}
                  {data.research_interests?.length > 0 && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Typography fontWeight={600} sx={{ mb: 1 }}>
                        Research areas <Typography component="span" variant="caption" color="text.secondary">(matched to funding call categories)</Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {data.research_interests.map((r, i) => (
                          <Tooltip key={i} title={`Interest weight ${r.weight}/10`}>
                            <Chip
                              label={r.name}
                              color={sel.research_interests[i] ? 'secondary' : 'default'}
                              variant={sel.research_interests[i] ? 'filled' : 'outlined'}
                              onClick={() => toggle('research_interests', i)}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
              {step === 'applying' && <LinearProgress sx={{ mt: 2 }} />}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={step === 'parsing' || step === 'applying'}>Cancel</Button>
          {isPublic && (step === 'upload' || step === 'parsing') && (
            <Button variant="contained" startIcon={<PublicIcon />} onClick={handleFetchPublic}
                    disabled={!consent || step === 'parsing' || !(ids.orcid.trim() || ids.google_scholar.trim() || ids.website.trim())}>
              Fetch my public profile
            </Button>
          )}
          {!isPublic && (step === 'upload' || step === 'parsing') && (
            <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={handleParse} disabled={!file || step === 'parsing'}>
              Analyse CV
            </Button>
          )}
          {(step === 'review' || step === 'applying') && (
            <Button variant="contained" onClick={handleApply} disabled={!selectedCount || step === 'applying'}>
              Add {selectedCount} item{selectedCount === 1 ? '' : 's'} to profile
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <PaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        productName="AI Credits"
        productDescription="Credits cover AI token costs. A CV import typically uses 1 credit."
        unitLabel="credits"
        costPerOperation={1}
        operationLabel="CV import"
        currentBalance={balance ?? 0}
        onSuccess={() => { refreshBalance(); setPaymentOpen(false); }}
      />
    </>
  );
}
